import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, groupBy, mergeMap, takeUntil } from 'rxjs/operators';
import { ConfigService, DataTablesService } from '../../../../../core';
import { ConfigCliente } from '../../../../configuracoes/app/cliente';
import { KPI_CUSTOMER_SHARE, KPI_RAP, KPI_RAR, KPI_SELL_IN_CLIENTE } from '../../../../kpis';
import { CustomerShareValueObject } from '../../../../pic/customer-share';
import { CustomerShareValorValueObject } from '../../../../pic/customer-share/domain';
import { ClienteAnaliseMercadoRAPClienteEventProps, ClienteAnaliseMercadoRAPClienteResponse, ClienteAnaliseMercadoRAPLegendaResponse, ClienteAnaliseMercadoRAPOrderBy, ClienteAnaliseMercadoRAPOrderByColumn, ClienteAnaliseMercadoRAPTotal } from '../../adapters';

@Component({
    selector: 'sgt-cliente-analise-mercado-rap-table',
    templateUrl: './cliente-analise-mercado-rap-table.component.html',
    styleUrls: ['./cliente-analise-mercado-rap-table.component.less']
})
export class ClienteAnaliseMercadoRAPTableComponent implements OnInit {
    configCliente: ConfigCliente;

    /** Se table está em modo de revisão */
    @Input() revisaoMode = false;
    /** Análises de Mercado dos clientes */
    @Input() clientes: ClienteAnaliseMercadoRAPClienteResponse[];
    /** Totais das Análises de Mercado */
    @Input() total: ClienteAnaliseMercadoRAPTotal;
    /** Legand das Análises de Mercado */
    @Input() legenda: ClienteAnaliseMercadoRAPLegendaResponse;
    /** Se é a última página */
    @Input() isUltimaPagina = false;
    /** Se a consulta está vazia */
    @Input() isEmpty = false;
    /** Ao alterar algum valor de uma Análise de Mercado */
    @Output() onChange = new EventEmitter<ClienteAnaliseMercadoRAPClienteEventProps>();
    /** Subject das alterações dos lançamentos dos clientes */
    private changesSubject = new Subject<ClienteAnaliseMercadoRAPClienteEventProps>();

    /** Ao realizar o Infinite Scroll */
    @Output() onInfiniteScroll = new EventEmitter<void>();
    /** Ao alterar o Order By */
    @Output() orderByChange = new EventEmitter<ClienteAnaliseMercadoRAPOrderBy>();


    /** Evento para abrir a Análise de Mercado do cliente */
    @Output() openAnaliseMercado = new EventEmitter<ClienteAnaliseMercadoRAPClienteResponse>();
    /** Evento para abrir o cadastro do cliente */
    @Output() openCliente = new EventEmitter<ClienteAnaliseMercadoRAPClienteResponse>();
    /** Evento para pré-baixar o cliente */
    @Output() preBaixarCliente = new EventEmitter<ClienteAnaliseMercadoRAPClienteResponse>();
    /** Evento para baixar o cliente */
    @Output() baixarCliente = new EventEmitter<ClienteAnaliseMercadoRAPClienteResponse>();
    /** Evento para inserir uma compras no cliente */
    @Output() insertCentralCompra = new EventEmitter<void>();
    /** Evento para atualizar a central de compras */
    @Output() editCentralCompra = new EventEmitter<ClienteAnaliseMercadoRAPClienteResponse>();
    /** Evento para inserir um participante em uma compras no cliente */
    @Output() insertParticipanteCentralCompra = new EventEmitter<ClienteAnaliseMercadoRAPClienteResponse>();

    @ViewChild('container') container: ElementRef;

    // Datatable
    isCreatingDataTable: boolean
    idDataTable = 'clienteAnaliseMercadoRAPTable';
    dataTableFooterColspan: number;
    private dataTable: any;

    // KPIs
    KPI_SELL_IN_CLIENTE = KPI_SELL_IN_CLIENTE;
    KPI_RAP = KPI_RAP;
    KPI_RAR = KPI_RAR;
    KPI_CUSTOMER_SHARE = KPI_CUSTOMER_SHARE;

    private destroy$ = new Subject<void>();

    constructor(private configService: ConfigService,
                private dataTablesService: DataTablesService) {}

    ngOnInit() {
        this.configCliente = this.configService.getCliente();
        this.createChangesSubject();
        // this.createTable();
    }

    /** Cria o subject das alterações dos lançamentos dos clientes */
    private createChangesSubject() {
        this.changesSubject.pipe(
            // Agrupa as alterações por cliente
            groupBy(cliente => cliente.id),

            mergeMap(group => group.pipe(
                debounceTime(3000), // 3s
                distinctUntilChanged((prev, curr) =>
                    // Somente emite evento de alteração quando houver alguma alteração
                     prev.valorPotencial === curr.valorPotencial &&
                           prev.customerShareRAP === curr.customerShareRAP &&
                           prev.valorPretendido === curr.valorPretendido
                )
            )),
            takeUntil(this.destroy$)
        ).subscribe(cliente => {
            this.onChange.emit(cliente);
        });
    }

    /** Cria as props do evento de alteração dos lançamentos dos clientes */
    private createChangesProps(cliente: ClienteAnaliseMercadoRAPClienteResponse): ClienteAnaliseMercadoRAPClienteEventProps {
        if (!cliente) {
            return null;
        }

        return {
            id: cliente.idCliente,
            tipoPessoa: cliente.tipoPessoa,
            codigoIntegracaoErp: cliente.codigoIntegracaoErpCliente,
            nome: cliente.nomeCliente,
            nomeFantasia: cliente.nomeFantasiaCliente,
            valorPotencial: cliente.valorPotencial,
            customerShareRAP: cliente.customerShareRAP,
            valorPretendido: cliente.valorPretendido
        }
    }

    /** Cria a dataTable com pesquisa das análises de mercados dos clientes */
    private createTable(): Promise<void> {
        return new Promise<void>((resolve) => {
            const columns: any[] = [
                {
                    data: null,
                    className: 'col-options text-center',
                },
                {
                    data: null,
                    className: 'col-nome-cliente',
                    serverOrderByColumn: ClienteAnaliseMercadoRAPOrderByColumn.NOME_CLIENTE
                }
            ];

            if (this.revisaoMode) {
                columns.push(...[
                    {
                        data: null,
                        className: 'col-rar-ano-anterior',
                        serverOrderByColumn: ClienteAnaliseMercadoRAPOrderByColumn.VALOR_REALIZADO_ANO_ANTERIOR2
                    },
                    {
                        data: null,
                        className: 'col-rar-ano-anterior',
                        serverOrderByColumn: ClienteAnaliseMercadoRAPOrderByColumn.VALOR_REALIZADO_ANO_ANTERIOR1
                    },
                    {
                        data: null,
                        className: 'col-rap-ano-anterior',
                        serverOrderByColumn: ClienteAnaliseMercadoRAPOrderByColumn.VALOR_PRETENDIDO_ANO_ANTERIOR1
                    }
                ]);
            }

            columns.push(...[
                {
                    data: null,
                    className: 'col-sell-in-cliente',
                    serverOrderByColumn: ClienteAnaliseMercadoRAPOrderByColumn.VALOR_POTENCIAL
                },
                {
                    data: null,
                    className: 'col-customer-share-rap-perc',
                    serverOrderByColumn: ClienteAnaliseMercadoRAPOrderByColumn.CUSTOMER_SHARE_RAP
                },
                {
                    data: null,
                    className: 'col-customer-share-rap-valor',
                    serverOrderByColumn: ClienteAnaliseMercadoRAPOrderByColumn.VALOR_PRETENDIDO
                },
                {
                    data: null,
                    className: 'col-nome-classificacao-rap',
                    serverOrderByColumn: ClienteAnaliseMercadoRAPOrderByColumn.CLASSIFICACAO_RAP
                },
                {
                    data: null,
                    className: 'col-customer-share-rar-perc',
                    serverOrderByColumn: ClienteAnaliseMercadoRAPOrderByColumn.CUSTOMER_SHARE_RAR
                },
                {
                    data: null,
                    className: 'col-customer-share-rar-valor',
                    serverOrderByColumn: ClienteAnaliseMercadoRAPOrderByColumn.VALOR_REALIZADO
                },
                {
                    data: null,
                    className: 'col-nome-classificacao-rar',
                    serverOrderByColumn: ClienteAnaliseMercadoRAPOrderByColumn.CLASSIFICACAO_RAR
                }
            ]);

            this.dataTablesService.showTable(`#${this.idDataTable}`, this.dataTable, {
                columns,
                searching: false,
                info: false,
                paging: false,
                processing: true
            }).then(dataTable => {
                this.dataTable = dataTable;

                // Adicionando evento de ordenação das colunas
                setTimeout(() => {
                    this.dataTable.on('order.dt', () => {
                        // Emitindo evento de alteração no order by
                        const tableOrderBy = this.dataTable.order();
                        const dataTableColumn = columns[tableOrderBy[0][0]];

                        const orderBy: ClienteAnaliseMercadoRAPOrderBy = {
                            column: dataTableColumn?.serverOrderByColumn,
                            dir: tableOrderBy[0][1]
                        };
                        this.orderByChange.emit(orderBy);
                    });
                }, 1000);


                resolve();
            })
            .catch(() => {});
        });
    }

    /** Ao alterar o Sell In Cliente (Valor potencial) */
    onChangeSellInCliente(cliente: ClienteAnaliseMercadoRAPClienteResponse) {
        if (!cliente) {
            return;
        }

        // Calculando o Customer Share RAP
        const vo = new CustomerShareValueObject(this.configCliente.customerShareOtimo, this.configCliente.customerShareBom, this.configCliente.customerShareRazoavel, cliente.valorPotencial, cliente.valorPretendido, null);
        cliente.customerShareRAP = vo.valorPretendidoPerc;
        cliente.indicadorCustomerShareRAP = vo.indicadorPretendido.id;

        // Se não possuir o percentual de Customer Share, removemos o valor do Customer Share
        if (!cliente.customerShareRAP || cliente.customerShareRAP <= 0) {
            cliente.valorPretendido = null;
        }


        // Emitindo alteração no cliente
        this.changesSubject.next(this.createChangesProps(cliente));
    }

    /** Ao alterar o Customer Share RAP */
    onChangeCustomerShareRAP(cliente: ClienteAnaliseMercadoRAPClienteResponse) {
        if (!cliente) {
            return;
        }

        // Calculando o valor do Customer Share RAP
        const voValor = new CustomerShareValorValueObject(cliente.valorPotencial, cliente.customerShareRAP);
        cliente.valorPretendido = voValor.valor;

        // Calculando o indicador Customer Share RAP
        const vo = new CustomerShareValueObject(this.configCliente.customerShareOtimo, this.configCliente.customerShareBom, this.configCliente.customerShareRazoavel, cliente.valorPotencial, cliente.valorPretendido, null);
        cliente.indicadorCustomerShareRAP = vo.indicadorPretendido.id;

        // Emitindo alteração no cliente
        this.changesSubject.next(this.createChangesProps(cliente));
    }

    /** Ao alterar o valor do Customer Share RAP */
    onChangeCustomerShareValorRAP(cliente: ClienteAnaliseMercadoRAPClienteResponse) {
        if (!cliente) {
            return;
        }

        // Calculando o Customer Share RAP
        const vo = new CustomerShareValueObject(this.configCliente.customerShareOtimo, this.configCliente.customerShareBom, this.configCliente.customerShareRazoavel, cliente.valorPotencial, cliente.valorPretendido, null);
        cliente.customerShareRAP = vo.valorPretendidoPerc;
        cliente.indicadorCustomerShareRAP = vo.indicadorPretendido.id;

        // Emitindo alteração no cliente
        this.changesSubject.next(this.createChangesProps(cliente));
    }

    /** Destroy a datatable */
    private destroyTable() {
        this.dataTable.off('order.dt');
        this.dataTablesService.destroyTable(this.dataTable);
    }

    /** Reseta o table view */
    reset() {
        // Retornando scroll ao top do table container
        jQuery(this.container.nativeElement).scrollTop(0);
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        // this.destroyTable();
    }
}
